import { useEffect, useMemo, useState } from "react";
import "./clients.css";
import { useHomeContent } from "../../hooks/useHomeContent";
import { SectionTitle } from "../../../../components/landing/ui/SectionTitle/SectionTitle";
import { Slider } from "../../../../components/landing/ui/Slider/Slider";

type ClientItem = {
  id?: string;
  logo: string;
  alt?: string;
};

const MOBILE_MAX = 768;

function useIsMobile(maxWidth = MOBILE_MAX) {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const calc = () => setIsMobile(window.innerWidth <= maxWidth);
    calc();
    window.addEventListener("resize", calc);
    return () => window.removeEventListener("resize", calc);
  }, [maxWidth]);

  return isMobile;
}

function splitToTwoRows<T>(arr: T[]) {
  const top: T[] = [];
  const bottom: T[] = [];
  arr.forEach((item, i) => (i % 2 === 0 ? top : bottom).push(item));
  return { top, bottom };
}

function MarqueeRow({
  items,
  direction,
  durationSec = 38,
  defaultAlt,
}: {
  items: ClientItem[];
  direction: "left" | "right";
  durationSec?: number;
  defaultAlt: string;
}) {
  const doubled = useMemo(() => [...items, ...items], [items]);

  return (
    <div
      className={`oc-clients__row oc-clients__row--${direction}`}
      style={{ ["--duration" as any]: `${durationSec}s` }}
    >
      <div className="oc-clients__track">
        {doubled.map((c, idx) => (
          <div className="oc-clients__card" key={`${c.logo}-${idx}`}>
            <img
              className="oc-clients__logo"
              src={c.logo}
              alt={c.alt ?? defaultAlt}
              loading="lazy"
            />
          </div>
        ))}
      </div>
    </div>
  );
}

export const Clients = () => {
  const data = useHomeContent();
  const isMobile = useIsMobile();
  const sliderAria = data?.common?.sliderAria;

  const clientsBlock = data?.clients;
  const clients: ClientItem[] = Array.isArray(clientsBlock)
    ? clientsBlock
    : clientsBlock?.items ?? [];
  const title =
    (!Array.isArray(clientsBlock) && clientsBlock?.title) ??
    data?.clientsTitle ??
    "";

  const defaultAlt =
    (!Array.isArray(clientsBlock) && clientsBlock?.logoAltFallback) || "logo";

  if (!clients.length) return null;

  const { top, bottom } = splitToTwoRows(clients);
  const mobileTop = top.length ? top : clients.slice(0, 6);
  const mobileBottom = bottom.length ? bottom : clients.slice(6, 12);

  return (
    <section className="oc-clients oc-section">
      <div className="oc-container">
        <SectionTitle title={title} />

        {isMobile ? (
          <div className="oc-clients__mobileGrid">
            <div className="oc-clients__mobileRow">
              <Slider<ClientItem>
                items={mobileTop}
                renderItem={(c) => (
                  <div className="oc-clients__card oc-clients__card--mobile">
                    <img
                      className="oc-clients__logo"
                      src={c.logo}
                      alt={c.alt ?? defaultAlt}
                      loading="lazy"
                    />
                  </div>
                )}
                showArrows={false}
                showDots={false}
                loop
                swipe
                autoPlay
                autoPlayIntervalMs={6000}
                slidesToShow={3}
                scrollBy={1}
                gap={14}
                ariaLabels={sliderAria}
              />
            </div>

            <div className="oc-clients__mobileRow">
              <Slider<ClientItem>
                items={mobileBottom}
                renderItem={(c) => (
                  <div className="oc-clients__card oc-clients__card--mobile">
                    <img
                      className="oc-clients__logo"
                      src={c.logo}
                      alt={c.alt ?? defaultAlt}
                      loading="lazy"
                    />
                  </div>
                )}
                showArrows={false}
                showDots={false}
                loop
                swipe
                autoPlay
                autoPlayIntervalMs={6000}
                slidesToShow={3}
                scrollBy={1}
                gap={14}
                ariaLabels={sliderAria}
              />
            </div>
          </div>
        ) : (
          <div className="oc-clients__desktop">
            <div className="oc-clients__marquee">
              <MarqueeRow
                items={top}
                direction="right"
                durationSec={40}
                defaultAlt={defaultAlt}
              />
              <MarqueeRow
                items={bottom}
                direction="left"
                durationSec={40}
                defaultAlt={defaultAlt}
              />
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
